import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { ApiError } from '@/api/client';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useClientDataGrid } from '@/components/DataGrid/useClientDataGrid';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  isMetadataKey,
  translationStatus,
  useAddLocaleKey,
  useClearLocalizationCache,
  useCultures,
  useDeleteLocaleKey,
  useLocaleFile,
  useLocaleFiles,
  useUpdateLocaleFile,
  type TranslationStatus,
} from './useLocalization';

/**
 * Replaces `Pages/Admin/LocalizationManagement.cshtml` at `/admin/localization`.
 *
 * A locale file holds every culture at once, so the culture tabs choose which
 * column of the same document the Value column shows — they do not switch files.
 * Editing a key opens all cultures together, which is how the Razor add-key
 * modal worked and is the only way to see whether a key is actually complete.
 */

/** One row of the table: a key, its value in the active culture, its status. */
interface TranslationRow {
  key: string;
  value: string;
  status: TranslationStatus;
}

type FilterMode = 'all' | 'missing' | 'complete';

function errorText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const problem = error.problem as { error?: string; message?: string } | null;
  return problem?.error ?? problem?.message ?? error.message;
}

export function LocalizationManagementPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const { data: culturesData } = useCultures();
  const { data: files } = useLocaleFiles();

  const cultureCodes = useMemo(
    () => (culturesData?.cultures ?? []).map((culture) => culture.code),
    [culturesData],
  );

  const [fileName, setFileName] = useState<string | null>(null);
  const [activeCulture, setActiveCulture] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [newKeyName, setNewKeyName] = useState('');

  const { data: file, isLoading } = useLocaleFile(fileName);
  const updateFile = useUpdateLocaleFile();
  const addKey = useAddLocaleKey();
  const deleteKey = useDeleteLocaleKey();
  const clearCache = useClearLocalizationCache();

  // Default to the first file and the default culture once both have arrived.
  useEffect(() => {
    if (fileName == null && files && files.length > 0) setFileName(files[0]);
  }, [files, fileName]);

  useEffect(() => {
    if (activeCulture == null && culturesData) {
      setActiveCulture(culturesData.defaultCulture ?? culturesData.cultures[0]?.code ?? null);
    }
  }, [culturesData, activeCulture]);

  const rows = useMemo<TranslationRow[]>(() => {
    const translations = file?.translations ?? {};
    const culture = activeCulture ?? '';

    return Object.entries(translations)
      .filter(([key]) => !isMetadataKey(key))
      .map(([key, values]) => ({
        key,
        value: values?.[culture] ?? '',
        status: translationStatus(values, cultureCodes),
      }))
      .filter((row) => (filterMode === 'all' ? true : row.status === filterMode))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [file, activeCulture, cultureCodes, filterMode]);

  const stats = useMemo(() => {
    const translations = file?.translations ?? {};
    const keys = Object.keys(translations).filter((key) => !isMetadataKey(key));
    const missing = keys.filter(
      (key) => translationStatus(translations[key], cultureCodes) === 'missing',
    ).length;
    return { total: keys.length, missing };
  }, [file, cultureCodes]);

  /**
   * Culture codes present in the file but not in `SupportedCultures`.
   *
   * Worth surfacing rather than ignoring: the shipped locale files key English
   * as `en-US` while this application is configured for `en-GB`, so every key
   * counts as incomplete and the whole table reads "Missing" with no visible
   * cause. Naming the stray codes turns that into a diagnosis.
   */
  const strayCultures = useMemo(() => {
    const configured = new Set(cultureCodes);
    const found = new Set<string>();
    for (const values of Object.values(file?.translations ?? {})) {
      for (const code of Object.keys(values ?? {})) {
        if (!configured.has(code)) found.add(code);
      }
    }
    return Array.from(found).sort();
  }, [file, cultureCodes]);

  const { gridProps } = useClientDataGrid<TranslationRow>({
    rows,
    initialPageSize: 25,
    initialSorting: [{ id: 'key', desc: false }],
    search: debouncedSearch,
    searchFields: ['key', 'value'],
  });

  const openEdit = useCallback(
    (key: string) => {
      const values = file?.translations[key] ?? {};
      setDraft(Object.fromEntries(cultureCodes.map((code) => [code, values[code] ?? ''])));
      setEditingKey(key);
    },
    [file, cultureCodes],
  );

  const openAdd = useCallback(() => {
    setDraft(Object.fromEntries(cultureCodes.map((code) => [code, ''])));
    setNewKeyName('');
    setAddingKey(true);
  }, [cultureCodes]);

  const closeModals = useCallback(() => {
    setEditingKey(null);
    setAddingKey(false);
    updateFile.reset();
    addKey.reset();
  }, [updateFile, addKey]);

  /**
   * Saves an edited key by writing the whole document back — the update endpoint
   * takes the full translations map, so the draft is merged into what was loaded
   * rather than sent alone.
   */
  const handleSaveEdit = useCallback(() => {
    if (!fileName || !file || !editingKey) return;
    updateFile.mutate(
      {
        fileName,
        translations: { ...file.translations, [editingKey]: draft },
      },
      {
        onSuccess: () => {
          setNotice({ tone: 'success', text: t('Localization.Saved', 'Translation saved.') });
          closeModals();
        },
      },
    );
  }, [fileName, file, editingKey, draft, updateFile, closeModals, t]);

  const handleAddKey = useCallback(() => {
    if (!fileName || !newKeyName.trim()) return;
    addKey.mutate(
      { fileName, key: newKeyName.trim(), values: draft },
      {
        onSuccess: () => {
          setNotice({ tone: 'success', text: t('Localization.KeyAdded', 'Key added.') });
          closeModals();
        },
      },
    );
  }, [fileName, newKeyName, draft, addKey, closeModals, t]);

  const handleDelete = useCallback(
    async (key: string) => {
      if (!fileName) return;

      const confirmed = await confirm({
        variant: 'danger',
        icon: 'trash-2',
        title: t('crud:Crud.Delete.ConfirmTitle', 'Confirm deletion'),
        message: t('Localization.ConfirmDelete', 'Delete the key "{{key}}"?', { key }),
        confirmLabel: t('Button.Delete', 'Delete'),
      });
      if (!confirmed) return;

      // Both outcomes stay in the page's notice bar, where every other action on
      // this screen reports itself.
      deleteKey.mutate(
        { fileName, key },
        {
          onSuccess: () =>
            setNotice({ tone: 'success', text: t('Localization.KeyDeleted', 'Key deleted.') }),
          onError: (error) =>
            setNotice({
              tone: 'danger',
              text: errorText(error) ?? t('Localization.DeleteError', 'Could not delete the key.'),
            }),
        },
      );
    },
    [fileName, confirm, deleteKey, t],
  );

  const columns = useMemo<ColumnDef<TranslationRow, unknown>[]>(
    () => [
      {
        id: 'key',
        accessorKey: 'key',
        header: t('Localization.Key', 'Key'),
        cell: ({ row }) => <code className="text-body">{row.original.key}</code>,
      },
      {
        id: 'value',
        accessorKey: 'value',
        header: t('Localization.Value', 'Value'),
        cell: ({ row }) =>
          row.original.value ? (
            row.original.value
          ) : (
            <span className="text-muted fst-italic">
              {t('Localization.NotTranslated', 'Not translated')}
            </span>
          ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('Localization.Status', 'Status'),
        meta: { align: 'center' },
        cell: ({ row }) =>
          row.original.status === 'complete' ? (
            <span className="badge bg-success">{t('Localization.Complete', 'Complete')}</span>
          ) : (
            <span
              className="badge bg-warning"
              title={t('Localization.MissingHint', 'At least one culture has no value')}
            >
              {t('Localization.Missing', 'Missing')}
            </span>
          ),
      },
      {
        id: 'actions',
        header: () => null,
        enableSorting: false,
        meta: { align: 'end' },
        cell: ({ row }) => (
          <div className="btn-group btn-group-sm">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => openEdit(row.original.key)}
              title={t('Localization.Edit', 'Edit translation')}
              aria-label={t('Localization.Edit', 'Edit translation')}
            >
              <SpriteIcon name="edit" size={14} />
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => void handleDelete(row.original.key)}
              disabled={deleteKey.isPending}
              title={t('Localization.Delete', 'Delete key')}
              aria-label={t('Localization.Delete', 'Delete key')}
            >
              <SpriteIcon name="trash-2" size={14} />
            </button>
          </div>
        ),
      },
    ],
    [t, openEdit, handleDelete, deleteKey.isPending],
  );

  const modalError = errorText(updateFile.error ?? addKey.error);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3 mb-1">{t('Localization.Title', 'Translations')}</h1>
          <p className="text-muted mb-0">
            {t('Localization.Subtitle', 'Interface text for every supported language.')}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() =>
              clearCache.mutate(undefined, {
                onSuccess: () =>
                  setNotice({
                    tone: 'success',
                    text: t('Localization.CacheCleared', 'Server translation cache cleared.'),
                  }),
              })
            }
            disabled={clearCache.isPending}
          >
            {t('Localization.ClearCache', 'Clear cache')}
          </button>
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center"
            onClick={openAdd}
            disabled={!fileName}
          >
            <SpriteIcon name="plus" size={16} className="sa-icon me-2" />
            {t('Localization.AddKey', 'Add key')}
          </button>
        </div>
      </div>

      {notice && (
        <div className={`alert alert-${notice.tone} alert-dismissible`} role="alert">
          {notice.text}
          <button type="button" className="btn-close" onClick={() => setNotice(null)} />
        </div>
      )}

      {strayCultures.length > 0 && (
        <div className="alert alert-warning" role="alert">
          {t(
            'Localization.StrayCultures',
            'This file also holds values for {{codes}}, which are not among the configured cultures ({{configured}}). Keys are counted as incomplete until every configured culture has a value.',
            { codes: strayCultures.join(', '), configured: cultureCodes.join(', ') },
          )}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1" htmlFor="loc-file">
                {t('Localization.File', 'File')}
              </label>
              <select
                id="loc-file"
                className="form-select"
                value={fileName ?? ''}
                onChange={(e) => setFileName(e.target.value)}
              >
                {(files ?? []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label small text-muted mb-1" htmlFor="loc-search">
                {t('Localization.Search', 'Search')}
              </label>
              <div className="input-group">
                <input
                  id="loc-search"
                  className="form-control"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('Localization.SearchPlaceholder', 'Key or value…')}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSearch('')}
                  title={t('Localization.ClearSearch', 'Clear search')}
                  aria-label={t('Localization.ClearSearch', 'Clear search')}
                >
                  <SpriteIcon name="x" size={14} />
                </button>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label small text-muted mb-1" htmlFor="loc-filter">
                {t('Localization.Filter', 'Filter')}
              </label>
              <select
                id="loc-filter"
                className="form-select"
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value as FilterMode)}
              >
                <option value="all">{t('Localization.AllTranslations', 'All translations')}</option>
                <option value="missing">{t('Localization.MissingOnly', 'Missing translations')}</option>
                <option value="complete">
                  {t('Localization.CompleteOnly', 'Complete translations')}
                </option>
              </select>
            </div>

            <div className="col-md-2 text-md-end">
              <div className="small text-muted mb-1">
                {t('Localization.Statistics', 'Statistics')}
              </div>
              <span className="badge bg-primary me-1">
                {t('Localization.KeyCount', '{{count}} keys', { count: stats.total })}
              </span>
              <span className="badge bg-warning">
                {t('Localization.MissingCount', '{{count}} missing', { count: stats.missing })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-body-tertiary py-2">
          <ul className="nav nav-tabs card-header-tabs" role="tablist">
            {(culturesData?.cultures ?? []).map((culture) => (
              <li className="nav-item" role="presentation" key={culture.code}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeCulture === culture.code}
                  className={`nav-link${activeCulture === culture.code ? ' active' : ''}`}
                  onClick={() => setActiveCulture(culture.code)}
                >
                  {culture.name}
                  <span className="text-muted ms-2 fs-sm">{culture.code}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <DataGrid<TranslationRow>
            {...gridProps}
            columns={columns}
            isLoading={isLoading}
            getRowId={(row) => row.key}
            pageSizeOptions={[25, 50, 100]}
            emptyMessage={t('Localization.Empty', 'No translations match this filter')}
          />
        </div>
      </div>

      {(editingKey != null || addingKey) && (
        <Modal show onHide={closeModals} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title as="h5">
              {addingKey
                ? t('Localization.AddKey', 'Add key')
                : t('Localization.EditKey', 'Edit translation')}
            </Modal.Title>
          </Modal.Header>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (addingKey) handleAddKey();
              else handleSaveEdit();
            }}
          >
            <Modal.Body>
              {modalError && (
                <div className="alert alert-danger" role="alert">
                  {modalError}
                </div>
              )}

              {addingKey ? (
                <div className="mb-3">
                  <label className="form-label" htmlFor="loc-newkey">
                    {t('Localization.KeyName', 'Key name')} <span className="text-danger">*</span>
                  </label>
                  <input
                    id="loc-newkey"
                    className="form-control"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    // Same rule the Razor form enforced: an identifier, because
                    // these become resource lookups.
                    pattern="^[a-zA-Z][a-zA-Z0-9_.]*$"
                    required
                    autoFocus
                  />
                  <div className="form-text">
                    {t(
                      'Localization.KeyNameHint',
                      'Letters, numbers, dots and underscores; must start with a letter.',
                    )}
                  </div>
                </div>
              ) : (
                <p className="mb-3">
                  <code className="text-body">{editingKey}</code>
                </p>
              )}

              <hr />
              <h6 className="mb-3">{t('Localization.Translations', 'Translations')}</h6>

              {/* Every culture at once: a key is only "complete" when they all
                  have a value, so editing one in isolation hides the gap. */}
              {(culturesData?.cultures ?? []).map((culture) => (
                <div className="mb-3" key={culture.code}>
                  <label className="form-label" htmlFor={`loc-val-${culture.code}`}>
                    {culture.name} <span className="text-muted fs-sm">({culture.code})</span>
                  </label>
                  <textarea
                    id={`loc-val-${culture.code}`}
                    className="form-control"
                    rows={2}
                    value={draft[culture.code] ?? ''}
                    onChange={(e) =>
                      setDraft((current) => ({ ...current, [culture.code]: e.target.value }))
                    }
                  />
                </div>
              ))}
            </Modal.Body>

            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={closeModals}>
                {t('Cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateFile.isPending || addKey.isPending}
              >
                {updateFile.isPending || addKey.isPending
                  ? t('Saving', 'Saving…')
                  : t('Save', 'Save')}
              </button>
            </Modal.Footer>
          </form>
        </Modal>
      )}
    </div>
  );
}
